import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';

import { useTradingOfferDetail } from './context/TradingOfferDetailContext';
import TradingOfferDetailHeader from './components/TradingOfferDetailHeader';
import TradingOfferStatusBanner from './components/TradingOfferStatusBanner';
import TradingOfferDetailSummaryCard from './components/TradingOfferDetailSummaryCard';
import TradingOfferPriceContextCard from './components/TradingOfferPriceContextCard';
import TradingOfferAcceptanceProgress from './components/TradingOfferAcceptanceProgress';
import TradingOfferOperationsTable from './components/TradingOfferOperationsTable';
import TradingOfferTimeline from './components/TradingOfferTimeline';
import TradingOfferSellerInfoCard from './components/TradingOfferSellerInfoCard';
import TradingOfferActionCard from './components/TradingOfferActionCard';

const TradingOfferDetail: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useTradingOfferDetail();

  if (!data.offer) {
    return (
      <Page id="trading-offer-detail-page">
        <div className="nk-block text-center py-5">
          <em className="icon ni ni-alert-circle fs-1 text-warning mb-3 d-block" />
          <h5>{t('OfferNotFound')}</h5>
          <p className="text-soft">{t('OfferNotFoundDescription')}</p>
          <button
            type="button"
            className="btn btn-primary mt-2"
            onClick={actions.handleGoBack}
          >
            {t('Back')}
          </button>
        </div>
      </Page>
    );
  }

  return (
    <Page id="trading-offer-detail-page">
      <TradingOfferDetailHeader
        offer={data.offer}
        offerReference={data.offerReference}
        offerStatus={data.offerStatus}
        societyId={data.societyId}
        societyName={data.societyName}
      />

      <div className="nk-block">
        <TradingOfferStatusBanner
          offer={data.offer}
          offerStatus={data.offerStatus}
        />

        <div className="row g-gs">
          <div className="col-lg-7">
            <TradingOfferDetailSummaryCard
              offer={data.offer}
              offerReference={data.offerReference}
              availableShares={data.availableShares}
              daysRemaining={data.daysRemaining}
            />

            <TradingOfferPriceContextCard priceContext={data.priceContext} />

            <TradingOfferAcceptanceProgress
              offer={data.offer}
              acceptanceHistory={data.acceptanceHistory}
            />

            <TradingOfferOperationsTable
              operations={data.tradingOfferOperations}
              onGoToOperation={actions.handleGoToOperation}
            />

            <TradingOfferTimeline steps={data.timelineSteps} />

            {data.sellerInfo && (
              <TradingOfferSellerInfoCard sellerInfo={data.sellerInfo} />
            )}
          </div>

          <div className="col-lg-5">
            <TradingOfferActionCard
              offer={data.offer}
              availableShares={data.availableShares}
              purchaseQuantity={data.purchaseQuantity}
              commissionRate={data.commissionRate}
              isExpired={data.isExpired}
              hasPartnerBuyer={data.hasPartnerBuyer}
              onQuantityChange={actions.handlePurchaseQuantityChange}
              onBuyShares={actions.handleBuyShares}
              onViewOtherOffers={actions.handleViewOtherOffers}
            />
          </div>
        </div>
      </div>

      {data.isLoading && <CustomLoading />}
    </Page>
  );
};

export default TradingOfferDetail;
